'use client'

import GenericForm from 'components/Form'
import { signIn } from 'next-auth/react'
import { object, string } from 'yup'

interface UserAuthFormProps {
    lang: string
    texts: {
        email: string
        password: string
        login: string
        loggingIn: string
        pleaseEnterYourPassword: string
        loggedInSuccessfullyRedirecting: string
        invalid_grant: string
        email_not_verified: string
    }
    className?: string
}

const LoginForm = ({ lang, texts }: UserAuthFormProps) => {
    const fields: TFormField[] = [
        {
            name: 'email',
            label: texts.email,
            type: 'email',
            required: true,
            placeholder: 'Enter your email'
        },
        {
            name: 'password',
            label: texts.password,
            type: 'password',
            required: true,
            placeholder: texts.pleaseEnterYourPassword
        }
    ]

    const onSubmit = async (formData: FormData): Promise<ApiResponse> => {
        const username = formData.get('email')?.toString()
        const password = formData.get('password')?.toString()
        if (!username || !password) return { success: false, message: 'Missing credentials' }

        let signInResult
        try {
            signInResult = await signIn('credentials', {
                username,
                password,
                redirect: false,
                callbackUrl: `/callbackUrl`
            })
        } catch (err) {
            const message =
                err instanceof Error && err.message
                    ? err.message
                    : 'Connection problem. Please check your network and try again.'
            return { success: false, message }
        }

        const errorCode = signInResult?.error
        const errorMessage =
            errorCode === 'email_not_verified'
                ? texts.email_not_verified
                : errorCode === 'CredentialsSignin'
                  ? texts.invalid_grant
                  : errorCode ?? texts.loggedInSuccessfullyRedirecting
        const result: ApiResponse = {
            success: signInResult?.ok ?? false,
            data: signInResult,
            error: errorCode ?? undefined,
            message: errorCode ? errorMessage : texts.loggedInSuccessfullyRedirecting
        }

        if (result.success) {
            // Wait for session cookie to be set by NextAuth, then do full page reload
            // This ensures RootLayout (server component) re-fetches the user via getUserSession
            // and updates SessionProvider, making user available to RightNavbarSliderMenu
            // Using a delay to ensure the cookie is properly set before redirect
            await new Promise(resolve => setTimeout(resolve, 500))

            // Full page reload ensures all server components re-run and read the session cookie
            window.location.href = `/${lang}/home`
        }
        return result
    }

    const loginSchema = object({
        email: string()
            .required('Please enter your email')
            .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Enter a valid email'),
        password: string().required('Enter your password')
    })

    return (
        <GenericForm
            fields={fields}
            onSubmit={onSubmit}
            buttonTexts={{ save: texts.login, saving: texts.loggingIn }}
            validationSchema={loginSchema}
            keepLoadingOnSuccess={true}
        />
    )
}
export default LoginForm
